import { useState } from "react";
import { Flex, Form, Input, Typography } from "antd";
import Button from "../Atoms/DynamicButton";
import AuthService from "../../Services/auth.service";
import SuccessResult from "../Atoms/SuccessResult/SuccessResult";
import FailureResult from "../Atoms/FailureResult/FailureResult";
import { useLoader } from "../../Context/LoaderContext/LoaderContext";

const failureReason = ["Entered email not found"];

interface ForgotPasswordFormProps {
  onBackToSignIn?: () => void;
  onSuccess?: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onBackToSignIn,
  onSuccess,
}) => {
  const { setLoading, isLoading } = useLoader();
  const authService = AuthService.getInstance();
  const [showFailure, setShowFailure] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const handleEmailSubmit = async (values: any) => {
    setLoading(true);
    const response = await authService.sendPasswordResetMail(values.email);
    if (response?.status === 201) {
      setLoading(false);
      setShowSuccess(true);
    } else {
      setLoading(false);
      setShowFailure(true);
    }
  };

  const handleTryAgain = () => {
    setShowFailure(false);
    setShowSuccess(false);
  };

  const handleOk = () => {
    onSuccess?.();
  };

  if (showSuccess && !showFailure) {
    return (
      <SuccessResult
        title="Mail Sent"
        subTitle="A password reset link has been sent to your email. Please check your inbox to reset your password."
        onClick={handleOk}
        buttonTitle="Home"
      />
    );
  }

  if (showFailure && !showSuccess) {
    return (
      <FailureResult
        title="Request Failed"
        subTitle="Your password reset request failed due to the following reasons:"
        reason={failureReason}
        onClick={handleTryAgain}
      />
    );
  }

  return (
    <Form
      name="forgot_password_form"
      className="auth-form"
      onFinish={handleEmailSubmit}
      autoComplete="off"
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Enter your registered email address and we'll send you a link to reset
        your password.
      </Typography.Paragraph>
      <Form.Item
        name="email"
        rules={[
          {
            required: true,
            type: "email",
            message: "Please enter a valid email address!",
          },
        ]}
      >
        <Input placeholder="Registered Email" />
      </Form.Item>
      <Flex gap={16} style={{ width: "100%" }}>
        <Button
          block
          type="default"
          onClick={onBackToSignIn}
        >
          Back to Sign In
        </Button>
        <Button
          block
          type="primary"
          htmlType="submit"
          loading={isLoading}
        >
          Send Reset Link
        </Button>
      </Flex>
    </Form>
  );
};

export default ForgotPasswordForm;
